"use client";

import { ProductCardGroup } from "@/src/components/ProductCard";
import { Tabs } from "@/src/components/Tabs";
import { Container, Stack, Typography } from "@mui/material";
import React, { useMemo, useState } from "react";
import { WebShopIcon } from "@/src/components/WebShopIcon";
import { useQuery } from "@tanstack/react-query";
import { getProductsList } from "@/src/api/services/products";
import { ProductGroup } from "@/src/shared/const";
import {PRODUCT_ANALYTICS, PRODUCT_EVENT_ANALYTICS} from "@/src/shared/config/productAnalytics";
import {useAnalytics} from "@/src/components/pages/StepPage/components/useAnalytics/useAnalytics";

const tabs: {
  label: string;
  code: ProductGroup;
  icon: JSX.Element;
  children: any[];
  analyticsCategory: string;
}[] = [
  {
    label: "Авто",
    code: ProductGroup.AUTO_INSURANCE,
    icon: (
      <WebShopIcon
        name="car"
        className="mb-0"
        width={40}
        height={40}
        strokeWidth={1.5}
        fill={"currentColor"}
      />
    ),
    children: [],
    analyticsCategory: "auto"
  },
  {
    label: "Подорож",
    code: ProductGroup.TOURISM,
    icon: (
      <WebShopIcon
        name="plane"
        className="mb-0"
        width={40}
        height={40}
        fill={"currentColor"}
      />
    ),
    children: [],
    analyticsCategory: "travel"
  },
  {
    label: "Майно",
    code: ProductGroup.PROPERTY,
    icon: (
      <WebShopIcon
        name="house"
        className="mb-0"
        width={40}
        height={40}
        fill={"currentColor"}
      />
    ),
    children: [],
    analyticsCategory: "property"
  },
];

const ProductsSection = () => {
  const { callAnalyticEvent } = useAnalytics();
  const [value, setValue] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    callAnalyticEvent({ eventName: "insurance_category_selected" }, { category: tabs[newValue]?.analyticsCategory, page_location: null });

    setValue(newValue);
  };

  const { data } = useQuery({
    queryKey: ["PRODUCTS"],
    queryFn: getProductsList,
    select: (data) => {
      return data.content
        .filter((item) => item.showOnMainPage !== false)
        .reduce(
        (acc: Record<ProductGroup, any>, item) => {
          const {
            id,
            title,
            slug,
            description,
            imageUrl,
            termsUrl,
            group,
            type,
            status,
          } = item;
          const actions = [];
          if (termsUrl) {
            const product_name: string | undefined = PRODUCT_ANALYTICS[slug];
            const eventName: string | undefined = PRODUCT_EVENT_ANALYTICS[slug];

            actions.push({
              className: "font-semibold",
              title: "Умови",
              variant: "outlined",
              href: termsUrl,
              analyticEvent: eventName ? [{ eventName },{ product_name, page_location: null }] : undefined,
              target: "_blank",
            });
          }
          if (group !== "PROPERTY") {
            const product_name: string | undefined = PRODUCT_ANALYTICS[slug];
            const eventName: string | undefined = PRODUCT_EVENT_ANALYTICS[slug];

            actions.push({
              className: "font-semibold",
              title: "Купити онлайн",
              variant: "contained",
              analyticEvent: eventName ? [{ eventName }, { product_name, page_location: null }] : undefined,
              href: `/products/${slug}`,
              disabled: status === "INACTIVE",
            });
          }
          acc[item.group as ProductGroup] = (
            acc[item.group as ProductGroup] || []
          ).concat({
            id,
            title,
            description,
            imageUrl,
            alt: title,
            actions,
          });
          return acc;
        },
        {} as Record<ProductGroup, any>,
      );
    },
  });

  const tabsValue = useMemo(() => {
    return tabs
      .map((item) => {
        item.children = data?.[item.code] || [];
        return item;
      })
      .filter((item) => item.children.length > 0);
  }, [data]);

  return (
    <section
      className={
        "bg-product-gradient pb-16 pt-10 backdrop-blur-xl max-md:py-10"
      }
    >
      <Container className={"mb-4"}>
        <Stack className={"flex flex-col justify-start gap-6 max-md:gap-3"}>
          <Typography
            component={"h2"}
            variant={"h2"}
            className={"text-[25px] text-dark-blue md:text-[45px]"}
          >
            {"Що бажаєте застрахувати?"}
          </Typography>
          <Tabs
            value={value}
            onChange={handleChange}
            tabs={tabsValue.map((item) => ({
              label: item.label,
              icon: item.icon,
            }))}
            variant="fullWidth"
          />
          <ProductCardGroup products={tabs[value].children as any} />
        </Stack>
      </Container>
    </section>
  );
};

export default ProductsSection;
